import { DomainLayer } from "@iventis/map-engine/src/state/map.state.types";
import { ObjectUpdate } from "@iventis/map-engine/src/types/events-store";
import { createMapFromArray } from "@iventis/utilities";
import { getWhat3WordsValue } from "@iventis/what3words";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { MapLayer } from "@iventis/domain-model/model/mapLayer";
import { LocalGeoJson, SelectedMapObject } from "@iventis/map-types";
import { MapObject } from "@iventis/domain-model/model/mapObject";
import { OptionalExceptFor } from "@iventis/types/useful.types";
import { Position } from "@turf/helpers";
import { map } from "./state/map.slice";
import { MapObjectUpdateDataField } from "./state/map.slice.types";
import { RootDispatch } from "./state/root.store";
import { GetGeoJsonResponse, patchMapObjectsDataFieldValues } from "./state/map.slice.thunks";

interface What3WordsObjectUpdate {
    objectId: string;
    layerId: string;
    what3wordsDataFieldId: string;
    coordinates: [number, number] | Position;
}

/** Gets the what3words value for the given objects and updates map objects locally */
export const getWhat3WordsValueForObjectUpdates = (objectUpdates: ObjectUpdate[], layers: MapLayer[], dispatch: RootDispatch, updateRemote = false) => {
    const layerMap = createMapFromArray(layers, "id");
    const updates: What3WordsObjectUpdate[] = objectUpdates.reduce((updates, creation) => {
        // Check if the layer has a What3Words datafield and if the geometry is a point
        const what3wordsDataField = layerMap.get(creation.layerId).dataFields.find((df) => df.type === DataFieldType.What3Words);
        if (what3wordsDataField != null && creation.geometry.type === "Point") {
            return [
                ...updates,
                { objectId: creation.objectId, layerId: creation.layerId, what3wordsDataFieldId: what3wordsDataField.id, coordinates: creation.geometry.coordinates },
            ];
        }
        return updates;
    }, [] as What3WordsObjectUpdate[]);
    patchMapObjectDataFieldValuesWithWhat3WordValue(updates, dispatch, updateRemote);
};

/** Checks an array of map objects has a what3words datafield value (if needed) and gets it if not */
export const checkIfMapObjectHasWhat3WordsValue = (objects: GetGeoJsonResponse[], layers: MapLayer[], dispatch: RootDispatch) => {
    const layerMap = createMapFromArray(layers, "id");
    const updates: What3WordsObjectUpdate[] = objects.reduce((updates, object) => {
        // Get the layer the map object is related to
        const layer = layerMap.get(object.layerId);
        const what3wordsDataField = layer.dataFields.find((df) => df.type === DataFieldType.What3Words);
        // Ensure it is a point and that the what3words value is not already set
        if (what3wordsDataField != null && object.feature.geometry.type === "Point" && object.feature?.properties[what3wordsDataField.id] == null) {
            return [
                ...updates,
                { coordinates: object.feature.geometry.coordinates, layerId: object.layerId, objectId: object.objectId, what3wordsDataFieldId: what3wordsDataField.id },
            ];
        }
        return updates;
    }, [] as What3WordsObjectUpdate[]);
    patchMapObjectDataFieldValuesWithWhat3WordValue(updates, dispatch);
};

/** Check if local selected map objects have a what3words value, if not update it */
export const updateSelectedMapObjectsWithNoWhat3WordsValue = (layers: MapLayer[], geojson: LocalGeoJson, selectedMapObjects: SelectedMapObject[], dispatch: RootDispatch) => {
    const updates: What3WordsObjectUpdate[] = [];
    layers.forEach((layer) => {
        // Get local geojson for each layer
        const layerMapObjects = geojson[layer.id];
        const what3wordsDataFieldId = layer.dataFields?.find((df) => df.type === DataFieldType.What3Words)?.id;
        if (what3wordsDataFieldId != null) {
            // For each local map object for a layer, check if it is selected and if it has a what3words value
            layerMapObjects?.forEach((mapObject) => {
                const isSelected = selectedMapObjects.some((selectedMapObject) => selectedMapObject.objectId === mapObject.objectId);
                const isMissingWhat3WordsValue = mapObject.feature.properties[what3wordsDataFieldId] == null;
                // If it is selected and missing a what3words value, update the what3words value
                if (isSelected && isMissingWhat3WordsValue && mapObject.feature.geometry.type === "Point") {
                    updates.push({ coordinates: mapObject.feature.geometry.coordinates, layerId: layer.id, objectId: mapObject.objectId, what3wordsDataFieldId });
                }
            });
        }
    });
    patchMapObjectDataFieldValuesWithWhat3WordValue(updates, dispatch);
};

const patchMapObjectDataFieldValuesWithWhat3WordValue = (updates: What3WordsObjectUpdate[], dispatch: RootDispatch, updateRemote = false) => {
    const completedUpdates: MapObjectUpdateDataField[] = [];
    updates.forEach((update) => {
        getWhat3WordsValue(update.coordinates[0], update.coordinates[1]).then((response) => {
            completedUpdates.push({
                layerId: update.layerId,
                object: { mapObjectId: update.objectId, dataFieldValues: { [update.what3wordsDataFieldId]: response } },
            });
            if (updates.length === completedUpdates.length && updateRemote) {
                dispatch(
                    patchMapObjectsDataFieldValues({
                        mapObjects: completedUpdates.map((o) => ({ id: o.object.mapObjectId, layerId: o.layerId, dataFieldValues: o.object.dataFieldValues })),
                    })
                );
            }
            if (updates.length === completedUpdates.length && !updateRemote) {
                dispatch(map.actions.patchMapObjectDataFieldValues(completedUpdates));
            }
        });
    });
};

/**
 * Sets objects what3words value to null when position on map changes, as what3words value is not longer correct.
 * This sends a patch request to the back end and updates the local store
 * */
export const patchWhat3WordsValueToNullRemote = (objectUpdates: ObjectUpdate[], layers: DomainLayer[], dispatch: RootDispatch) => {
    const layerMap = createMapFromArray(layers, "id");
    // Reset what3words value to null for each object
    const updates: OptionalExceptFor<MapObject, "id" | "layerId">[] = objectUpdates.reduce((objects, object) => {
        const layer = layerMap.get(object.layerId);
        const what3wordsDataField = layer.dataFields.find((df) => df.type === DataFieldType.What3Words);
        if (what3wordsDataField != null) {
            return [...objects, { layerId: object.layerId, id: object.objectId, dataFieldValues: { [what3wordsDataField.id]: null } }];
        }
        return objects;
    }, []);
    if (updates.length > 0) {
        dispatch(patchMapObjectsDataFieldValues({ mapObjects: updates }));
    }
};
